import { useEffect, useRef, useState } from "react";

const BASE_URL = "https://back-exporta.onrender.com";

const SECTIONS = [
  { id: "visao-geral", label: "Visão geral" },
  { id: "autenticacao", label: "Autenticação" },
  { id: "cotacao", label: "Cotação de frete" },
  { id: "pedidos", label: "Criar pedido" },
  { id: "erros", label: "Referência de erros" },
  { id: "checklist", label: "Checklist de integração" },
];

const ISO_CODES = [
  "US", "CA", "MX", "AR", "CL", "CO", "PY", "UY", "PE", "EC", "BO", "VE", "PA", "CR", "CU", "JM",
  "PT", "GB", "FR", "DE", "ES", "IT", "NL", "BE", "SE", "NO", "DK", "FI", "CH", "AT", "PL", "CZ",
  "HU", "RO", "GR", "TR", "IL", "AE", "SA", "EG", "MA", "ZA", "CN", "JP", "IN", "KR", "SG", "HK",
  "TW", "TH", "MY", "ID", "PH", "AU", "NZ", "RU",
];

function Method({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center rounded-md bg-brand-primary px-2.5 py-1 text-[12px] font-bold tracking-wide text-white">
      {children}
    </span>
  );
}

function StatusPill({ code }: { code: number }) {
  const ok = code < 400;
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[12px] font-medium ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
    >
      {code}
    </span>
  );
}

function CodeBlock({ id, label, code }: { id: string; label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLPreElement>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ref.current?.textContent || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard indisponível — ignora silenciosamente
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 shadow-sm">
      <div className="flex items-center justify-between bg-[#0E172B] px-3.5 py-2">
        <span className="text-[12px] text-slate-400">{label}</span>
        <button
          onClick={handleCopy}
          className="rounded-md border border-slate-700 px-2.5 py-1 text-[11.5px] text-slate-400 transition-colors hover:text-slate-100 hover:border-slate-500"
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
      <pre
        id={id}
        ref={ref}
        className="overflow-x-auto bg-[#0E172B] px-4 py-4 font-mono text-[13px] leading-relaxed text-slate-100"
      >
        {code}
      </pre>
    </div>
  );
}

function FieldsTable({
  rows,
}: {
  rows: { field: string; type: string; required?: boolean; desc: string }[];
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <table className="w-full text-left text-[14px]">
        <thead>
          <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <th className="pb-2.5 pr-3">Campo</th>
            <th className="pb-2.5 pr-3">Tipo</th>
            <th className="pb-2.5 pr-3">Obrigatório</th>
            <th className="pb-2.5">Descrição</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field} className="border-b border-slate-100 last:border-0 align-top">
              <td className="whitespace-nowrap py-2.5 pr-3 font-mono text-[13px] text-brand-primary">{r.field}</td>
              <td className="py-2.5 pr-3 font-mono text-[13px] text-slate-500">{r.type}</td>
              <td className="py-2.5 pr-3">
                {r.required ? (
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-red-500">Sim</span>
                ) : (
                  <span className="text-[11px] uppercase tracking-wide text-slate-400">Não</span>
                )}
              </td>
              <td className="py-2.5 text-slate-600">{r.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ApiDocsPage = () => {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 140;
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.offsetTop <= pos) current = s.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen w-full bg-brand-bg font-Inter text-brand-text">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 py-10 md:grid-cols-[220px_1fr] md:gap-12 md:py-14">
        {/* SIDEBAR */}
        <aside className="md:sticky md:top-10 md:self-start">
          <div className="mb-6 flex items-center gap-2">
            <img src="/images/intrex_logo.png" alt="Intrex" className="h-7 w-auto" />
          </div>
          <div className="mb-5 text-[13px] font-medium text-slate-500">Documentação de integração</div>
          <nav className="flex flex-col gap-0.5 border-l border-slate-200 pl-3.5">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`-ml-[15px] border-l-2 py-1.5 pl-[13px] text-[13.5px] transition-colors ${
                  active === s.id
                    ? "border-brand-primary font-medium text-brand-primary"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {s.label}
              </a>
            ))}
          </nav>
          <p className="mt-6 border-t border-slate-200 pt-4 text-[12px] text-slate-400">
            Confidencial — para uso exclusivo do time técnico responsável pela integração de checkout.
          </p>
        </aside>

        {/* MAIN */}
        <main className="flex min-w-0 flex-col gap-16">
          <section id="visao-geral" className="flex flex-col gap-5">
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-400">
              API pública · v1
            </div>
            <h1 className="max-w-2xl text-[32px] font-bold leading-tight text-brand-sidebar md:text-[38px]">
              Cotação de frete e envio de pedidos para a Intrex
            </h1>
            <p className="max-w-2xl text-[16px] text-slate-500">
              Este documento cobre as duas chamadas necessárias para integrar a plataforma de checkout ao
              processo de envio internacional da Intrex: exibir as opções de frete no checkout e, após o
              pagamento confirmado, transferir o pedido para processamento.
            </p>

            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 md:grid-cols-4">
              {[
                { k: "Base URL", v: BASE_URL },
                { k: "Autenticação", v: "Header x-api-key" },
                { k: "Formato", v: "application/json" },
                { k: "Endpoints", v: "2 rotas" },
              ].map((c) => (
                <div key={c.k} className="flex flex-col gap-1 bg-white p-4">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{c.k}</span>
                  <span className="break-all font-mono text-[13px] text-brand-sidebar">{c.v}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-1.5 text-[12px] font-bold tracking-wide text-brand-primary">PASSO 1</div>
                <h3 className="mb-1.5 text-[16.5px] font-semibold text-brand-sidebar">Cliente calcula o frete</h3>
                <p className="text-[14px] text-slate-500">
                  No checkout, com peso/dimensões do carrinho e o país de destino, chame{" "}
                  <code className="rounded bg-blue-50 px-1.5 py-0.5 text-[13px] text-brand-primary">
                    POST /api/public-quote
                  </code>
                  . A resposta traz as opções de transportadora já ordenadas por preço.
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-1.5 text-[12px] font-bold tracking-wide text-brand-primary">PASSO 2</div>
                <h3 className="mb-1.5 text-[16.5px] font-semibold text-brand-sidebar">Pedido é enviado à Intrex</h3>
                <p className="text-[14px] text-slate-500">
                  Assim que o pagamento é confirmado, chame{" "}
                  <code className="rounded bg-blue-50 px-1.5 py-0.5 text-[13px] text-brand-primary">
                    POST /api/public-orders
                  </code>
                  . A Intrex assume o pedido a partir daí.
                </p>
              </div>
            </div>
          </section>

          <section id="autenticacao" className="flex flex-col gap-4">
            <div className="max-w-2xl">
              <h2 className="mb-1.5 text-[23px] font-semibold text-brand-sidebar">Autenticação</h2>
              <p className="text-slate-500">
                As duas rotas exigem uma API key própria da sua integração, enviada no header{" "}
                <code className="rounded bg-blue-50 px-1.5 py-0.5 text-[13px] text-brand-primary">x-api-key</code>.
                A chave é gerada pelo time Intrex e vinculada à sua conta de cliente.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <table className="w-full text-left text-[14px]">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <th className="pb-2.5 pr-3">Situação</th>
                    <th className="pb-2.5">Resposta</th>
                  </tr>
                </thead>
                <tbody className="[&>tr]:border-b [&>tr]:border-slate-100 [&>tr:last-child]:border-0">
                  <tr>
                    <td className="py-2.5 pr-3">Header ausente</td>
                    <td className="py-2.5">
                      <StatusPill code={401} /> <span className="ml-2 text-slate-600">API key ausente</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Chave inválida ou desconhecida</td>
                    <td className="py-2.5">
                      <StatusPill code={401} /> <span className="ml-2 text-slate-600">API key inválida</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Conta suspensa ou inativa</td>
                    <td className="py-2.5">
                      <StatusPill code={403} /> <span className="ml-2 text-slate-600">Cliente inativo</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="rounded-r-lg border-l-[3px] border-brand-primary bg-blue-50 px-4 py-3 text-[14px] text-slate-700">
              <span className="mb-0.5 block text-[11.5px] font-semibold uppercase tracking-wide text-brand-primary">
                Importante
              </span>
              Trate a chave como uma senha: guarde em variável de ambiente no servidor, nunca no código-fonte do
              front-end do checkout.
            </div>
          </section>

          <section id="cotacao" className="flex flex-col gap-5">
            <div className="max-w-2xl">
              <div className="mb-2 flex flex-wrap items-center gap-2.5">
                <Method>POST</Method>
                <span className="font-mono text-[15px] text-brand-sidebar">/api/public-quote</span>
                <span className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11.5px] text-slate-400">
                  requer x-api-key
                </span>
              </div>
              <h2 className="mb-1.5 text-[23px] font-semibold text-brand-sidebar">Cotação de frete</h2>
              <p className="text-slate-500">
                Recebe peso, dimensões e destino, consulta UPS e FedEx em paralelo e devolve as opções
                disponíveis já ordenadas da mais barata para a mais cara.
              </p>
            </div>

            <div>
              <h3 className="mb-2 text-[16px] font-semibold text-brand-sidebar">Corpo da requisição</h3>
              <FieldsTable
                rows={[
                  { field: "peso", type: "string | number", required: true, desc: 'Peso em kg. Aceita vírgula ou ponto decimal (ex.: "2,5").' },
                  { field: "destino", type: "string", required: true, desc: 'Código ISO alpha-2 do país (ex.: "US"). Ver lista abaixo.' },
                  { field: "comprimento", type: "number", desc: "Centímetros. Padrão: 20." },
                  { field: "largura", type: "number", desc: "Centímetros. Padrão: 15." },
                  { field: "altura", type: "number", desc: "Centímetros. Padrão: 10." },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-[16px] font-semibold text-brand-sidebar">Exemplo — PHP puro (cURL)</h3>
                <CodeBlock
                  id="quote-req"
                  label="cotar-frete.php"
                  code={`<?php
$apiKey = getenv('INTREX_API_KEY');

$payload = [
    'peso'        => '2.5',
    'destino'     => 'US',
    'comprimento' => 30,
    'largura'     => 20,
    'altura'      => 15,
];

$ch = curl_init('${BASE_URL}/api/public-quote');
curl_setopt_array($ch, [
    CURLOPT_POST           => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER     => [
        'Content-Type: application/json',
        'x-api-key: ' . $apiKey,
    ],
    CURLOPT_POSTFIELDS => json_encode($payload),
]);

$response = curl_exec($ch);
$status   = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$data = json_decode($response, true);
// $data['options'] já vem ordenado do mais barato ao mais caro`}
                />
              </div>
              <div>
                <h3 className="mb-2 text-[16px] font-semibold text-brand-sidebar">Resposta — 200 OK</h3>
                <CodeBlock
                  id="quote-res"
                  label="application/json"
                  code={`{
  "ok": true,
  "options": [
    {
      "carrier": "UPS",
      "service": "Worldwide Expedited",
      "price": 45.20,
      "currency": "USD",
      "sla_days": 5,
      "label": "UPS - USD 45.20",
      "sla_label": "5 dias úteis"
    },
    {
      "carrier": "FedEx",
      "service": "International Connect Plus",
      "price": 51.30,
      "currency": "USD",
      "sla_days": 7,
      "label": "FedEx - USD 51.30",
      "sla_label": "7 dias úteis"
    }
  ]
}`}
                />
              </div>
            </div>

            <div>
              <h3 className="mb-1.5 text-[16px] font-semibold text-brand-sidebar">Destinos suportados (ISO alpha-2)</h3>
              <p className="mb-2.5 text-[13.5px] text-slate-500">
                56 países. Nomes em português também são aceitos, mas o código ISO é o formato recomendado.
              </p>
              <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8 md:grid-cols-10">
                {ISO_CODES.map((code) => (
                  <div
                    key={code}
                    className="rounded-md border border-slate-200 bg-white py-1.5 text-center font-mono text-[12px] text-slate-500"
                  >
                    {code}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="pedidos" className="flex flex-col gap-5">
            <div className="max-w-2xl">
              <div className="mb-2 flex flex-wrap items-center gap-2.5">
                <Method>POST</Method>
                <span className="font-mono text-[15px] text-brand-sidebar">/api/public-orders</span>
                <span className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11.5px] text-slate-400">
                  requer x-api-key
                </span>
              </div>
              <h2 className="mb-1.5 text-[23px] font-semibold text-brand-sidebar">Criar pedido</h2>
              <p className="text-slate-500">
                Chamada única após o pagamento confirmado no checkout. Cria o pedido na fila de processamento
                da Intrex com comprador, endereço de entrega e itens.
              </p>
            </div>

            <div>
              <h3 className="mb-2 text-[16px] font-semibold text-brand-sidebar">Corpo da requisição</h3>
              <FieldsTable
                rows={[
                  { field: "pedido_ref", type: "string", required: true, desc: "Identificador único do pedido na sua plataforma. Reenvios com a mesma referência são recusados." },
                  { field: "moeda", type: "string", desc: 'Código da moeda (ex.: "USD", "BRL").' },
                  { field: "comprador.nome", type: "string", desc: "Nome completo do destinatário." },
                  { field: "comprador.email", type: "string", desc: "E-mail de contato." },
                  { field: "comprador.telefone", type: "string", desc: "Telefone com DDI, se disponível." },
                  { field: "endereco.rua_numero", type: "string", desc: "Logradouro e número/complemento." },
                  { field: "endereco.cidade", type: "string", desc: "Cidade de entrega." },
                  { field: "endereco.estado", type: "string", desc: "Estado/província." },
                  { field: "endereco.cep", type: "string", desc: "Código postal." },
                  { field: "endereco.pais", type: "string", desc: "País de entrega (ISO alpha-2 recomendado)." },
                  { field: "itens", type: "array", required: true, desc: "Um item por linha do carrinho." },
                ]}
              />
            </div>

            <div>
              <h3 className="mb-2 text-[16px] font-semibold text-brand-sidebar">Campos de cada item</h3>
              <FieldsTable
                rows={[
                  { field: "sku", type: "string", desc: "SKU do produto." },
                  { field: "titulo", type: "string", desc: "Nome do produto." },
                  { field: "qty", type: "number", desc: "Quantidade." },
                  { field: "preco", type: "number", desc: "Preço unitário." },
                  { field: "categoria", type: "string", desc: "Categoria do produto, se houver." },
                  { field: "hscode", type: "string", desc: "Código HS/NCM para desembaraço aduaneiro, se conhecido." },
                  { field: "descricao", type: "string", desc: "Descrição usada na declaração de conteúdo." },
                  { field: "pesoUnit", type: "string", desc: "Peso unitário em kg." },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-[16px] font-semibold text-brand-sidebar">Exemplo — corpo da requisição</h3>
                <CodeBlock
                  id="order-req"
                  label="application/json"
                  code={`{
  "pedido_ref": "10234",
  "moeda": "USD",
  "comprador": {
    "nome": "John Doe",
    "email": "john@example.com",
    "telefone": "+1 555 010 0100"
  },
  "endereco": {
    "rua_numero": "123 Main St, Apt 4",
    "cidade": "New York",
    "estado": "NY",
    "cep": "10001",
    "pais": "US"
  },
  "itens": [
    {
      "sku": "CAM-AZUL-M",
      "titulo": "Camiseta Azul M",
      "qty": 2,
      "preco": 29.90,
      "categoria": "Vestuário",
      "hscode": "610910",
      "descricao": "Camiseta 100% algodão",
      "pesoUnit": "0.3"
    }
  ]
}`}
                />
              </div>
              <div className="flex flex-col gap-3.5">
                <h3 className="text-[16px] font-semibold text-brand-sidebar">Resposta — 200 OK</h3>
                <CodeBlock
                  id="order-res"
                  label="application/json"
                  code={`{
  "ok": true,
  "pedido_ref": "10234",
  "created": 1,
  "skipped": 0,
  "grouped_orders": 1
}`}
                />
                <div className="rounded-r-lg border-l-[3px] border-brand-primary bg-blue-50 px-4 py-3 text-[14px] text-slate-700">
                  <span className="mb-0.5 block text-[11.5px] font-semibold uppercase tracking-wide text-brand-primary">
                    Idempotência
                  </span>
                  Reenviar o mesmo <code className="rounded bg-white/60 px-1 py-0.5 text-[13px]">pedido_ref</code>{" "}
                  não duplica o pedido — a API responde <StatusPill code={409} /> e não recria o registro. Seguro
                  para tentar novamente em caso de timeout de rede.
                </div>
              </div>
            </div>
          </section>

          <section id="erros" className="flex flex-col gap-4">
            <div className="max-w-2xl">
              <h2 className="mb-1.5 text-[23px] font-semibold text-brand-sidebar">Referência de erros</h2>
              <p className="text-slate-500">
                Todas as respostas de erro seguem o formato{" "}
                <code className="rounded bg-blue-50 px-1.5 py-0.5 text-[13px] text-brand-primary">
                  {'{ "ok": false, "error": "..." }'}
                </code>
                .
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <table className="w-full text-left text-[14px]">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <th className="pb-2.5 pr-3">Status</th>
                    <th className="pb-2.5 pr-3">Quando ocorre</th>
                    <th className="pb-2.5">Endpoint</th>
                  </tr>
                </thead>
                <tbody className="[&>tr]:border-b [&>tr]:border-slate-100 [&>tr:last-child]:border-0">
                  {[
                    { code: 400, when: "Campo obrigatório ausente ou inválido (ex.: peso, destino, pedido_ref, itens).", ep: "ambos" },
                    { code: 401, when: "API key ausente ou inválida.", ep: "ambos" },
                    { code: 403, when: "Conta do cliente inativa ou suspensa.", ep: "public-orders" },
                    { code: 409, when: "pedido_ref já foi importado anteriormente.", ep: "public-orders" },
                    { code: 502, when: "Nenhuma transportadora respondeu à cotação no momento.", ep: "public-quote" },
                    { code: 500, when: "Erro interno inesperado.", ep: "ambos" },
                  ].map((r) => (
                    <tr key={r.code}>
                      <td className="py-2.5 pr-3">
                        <StatusPill code={r.code} />
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.when}</td>
                      <td className="py-2.5 font-mono text-[13px] text-slate-500">{r.ep}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="checklist" className="flex flex-col gap-4">
            <div className="max-w-2xl">
              <h2 className="mb-1.5 text-[23px] font-semibold text-brand-sidebar">Checklist de integração</h2>
              <p className="text-slate-500">Passos para sair de ambiente de testes para produção.</p>
            </div>
            <div className="flex max-w-2xl flex-col gap-2.5">
              {[
                { t: "Solicitar a API key", d: "Peça ao time Intrex a chave vinculada à sua conta de cliente — ela é diferente por integração." },
                { t: "Guardar a chave em variável de ambiente", d: "Nunca no repositório de código nem no front-end do checkout." },
                { t: "Implementar a cotação no passo de frete do checkout", d: "Use o destino em ISO alpha-2 sempre que possível." },
                { t: "Implementar o envio do pedido no webhook de pagamento aprovado", d: "Envie apenas após a confirmação do pagamento." },
                { t: "Tratar o status 409 como sucesso silencioso", d: "Evita duplicar pedidos em caso de reenvio automático por timeout." },
                { t: "Testar com um pedido real de baixo valor", d: "Antes de liberar em produção, valide o fluxo completo ponta a ponta." },
              ].map((c) => (
                <div key={c.t} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                  <div className="mt-1 h-[18px] w-[18px] shrink-0 rounded border-[1.5px] border-brand-primary" />
                  <div>
                    <p className="text-[14px] font-medium text-brand-sidebar">{c.t}</p>
                    <p className="text-[13px] text-slate-500">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <footer className="max-w-2xl border-t border-slate-200 pt-5 text-[13px] text-slate-400">
            Documento de integração — Intrex Shipping. Em caso de dúvidas sobre algum campo ou comportamento da
            API, procure o time técnico responsável por esta integração do lado da Intrex.
          </footer>
        </main>
      </div>
    </div>
  );
};

export default ApiDocsPage;
